import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import type { AttestationNode as AttestationNodeType } from "@/lib/pharmacyFirst/types";

export interface AttestationAnswer {
  attested: boolean;
  notes: string | null;
  conclusion: "optionA" | "optionB";
  conclusionLabel: string;
}

interface AttestationNodeProps {
  node: AttestationNodeType;
  isDarkMode?: boolean;
  stepNumber?: number;
  onAnswer: (answer: AttestationAnswer) => void;
}

export function AttestationNode({
  node,
  isDarkMode = false,
  stepNumber,
  onAnswer,
}: AttestationNodeProps) {
  const [attested, setAttested] = useState(false);
  const [notes, setNotes] = useState("");
  const [conclusion, setConclusion] = useState<"optionA" | "optionB" | null>(null);

  const canContinue = attested && conclusion !== null;

  const handleContinue = () => {
    if (!canContinue || conclusion === null) return;
    onAnswer({
      attested,
      notes: notes.trim().length > 0 ? notes.trim() : null,
      conclusion,
      conclusionLabel:
        conclusion === "optionA"
          ? node.conclusions.optionA.label
          : node.conclusions.optionB.label,
    });
  };

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
          >
            {stepNumber}
          </div>
        )}
        <div className="flex-1">
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.text}
          </h3>
          {node.hint && (
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              {node.hint}
            </p>
          )}
        </div>
      </div>

      <label
        className={`flex items-start gap-3 mb-4 cursor-pointer p-3 rounded-lg border ${attested
          ? isDarkMode
            ? "border-emerald-700 bg-emerald-950/30"
            : "border-emerald-200 bg-emerald-50"
          : isDarkMode
            ? "border-gray-600 bg-gray-750"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        <input
          type="checkbox"
          checked={attested}
          onChange={(e) => setAttested(e.target.checked)}
          aria-label={node.attestationStatement}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-emerald-600 cursor-pointer flex-shrink-0"
        />
        <span className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
          {(() => {
            const lines = node.attestationStatement.split("\n").filter(Boolean);
            const bullets = lines.filter((l) => l.startsWith("•"));
            const headers = lines.filter((l) => !l.startsWith("•"));
            return (
              <>
                {headers.map((line, i) => <p key={i} className="mb-1">{line}</p>)}
                {bullets.length > 0 && (
                  <ul className="list-disc space-y-1 mb-2">
                    {bullets.map((line, i) => (
                      <li key={i} className="ml-4">{line.slice(1).trim()}</li>
                    ))}
                  </ul>
                )}
              </>
            );
          })()}
        </span>
      </label>

      {node.notesPrompt && (
        <div className="mb-4">
          <label className={`text-xs font-medium block mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
            {node.notesPrompt}
            <span className={`ml-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              (optional)
            </span>
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes…"
            aria-label={node.notesPrompt}
            className={`w-full text-sm rounded-lg border px-3 py-2 resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${isDarkMode
              ? "border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-500"
              : "border-gray-300 bg-white text-gray-900 placeholder-gray-400"
            }`}
          />
        </div>
      )}

      <div className="mb-4">
        <p className={`text-xs font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
          Conclusion
        </p>
        <div className="space-y-2">
          {(["optionA", "optionB"] as const).map((opt) => {
            const label =
              opt === "optionA"
                ? node.conclusions.optionA.label
                : node.conclusions.optionB.label;
            const selected = conclusion === opt;

            return (
              <button
                key={opt}
                type="button"
                aria-pressed={selected}
                onClick={() => setConclusion(opt)}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 text-sm font-medium transition select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${selected
                  ? isDarkMode
                    ? "border-blue-500 bg-blue-950/40 text-blue-100"
                    : "border-blue-500 bg-blue-50 text-blue-900"
                  : isDarkMode
                    ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <Button className="w-full" onClick={handleContinue} disabled={!canContinue}>
        Continue
      </Button>
    </div>
  );
}
